import React, { useState, useCallback } from 'react';
import { Pressable, ScrollView, Alert, Platform, TextInput, Image, ActivityIndicator } from 'react-native';
import { 
  YStack, 
  XStack, 
  SizableText, 
  Button, 
  Card, 
  H3, 
  H4,
  Sheet,
  View
} from 'tamagui';
import { MaterialIcons, FontAwesome5 } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';

interface PostData {
  image: string | null;
  headline: string;
  caption: string;
  hashtags: string[];
  location?: string;
  isSponsored?: boolean;
}

interface PostDesignerProps {
  onSave?: (postData: PostData) => void;
  onPreview?: (postData: PostData) => void;
  onPublish?: (postData: PostData) => void;
  initialData?: Partial<PostData>;
}

const PostDesigner: React.FC<PostDesignerProps> = ({
  onSave,
  onPreview,
  onPublish,
  initialData
}) => {
  const theme = themeSelector();
  const [showPreview, setShowPreview] = useState(false);
  
  const [postData, setPostData] = useState<PostData>({
    image: initialData?.image || null,
    headline: initialData?.headline || '',
    caption: initialData?.caption || '',
    hashtags: initialData?.hashtags || [],
    location: initialData?.location || '',
    isSponsored: initialData?.isSponsored || false,
  });

  // Sync postData with initialData when it changes (e.g., when a post is selected)
  React.useEffect(() => {
    setPostData({
      image: initialData?.image || null,
      headline: initialData?.headline || '',
      caption: initialData?.caption || '',
      hashtags: initialData?.hashtags || [],
      location: initialData?.location || '',
      isSponsored: initialData?.isSponsored || false,
    });
  }, [initialData]);

  const [isUploading, setIsUploading] = useState(false);
  const [editingMode, setEditingMode] = useState<'none' | 'caption' | 'headline' | 'location' | 'hashtags'>('none');
  const [tempValue, setTempValue] = useState('');
  const [showMediaSelection, setShowMediaSelection] = useState(false);
  const [showAIPrompt, setShowAIPrompt] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // Show media selection modal
  const handleImagePress = useCallback(() => {
    setShowMediaSelection(true);
  }, []);

  // Upload image from device
  const uploadFromDevice = useCallback(async () => {
    try {
      setShowMediaSelection(false);
      setIsUploading(true);
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      
      if (status !== 'granted') {
        Alert.alert('Permission needed', 'Please grant camera roll permissions to upload images.');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets[0]) {
        setPostData(prev => ({
          ...prev,
          image: result.assets[0].uri
        }));
      }
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, []);

  // Generate image via AI
  const generateViaAI = useCallback(() => {
    setShowMediaSelection(false);
    setShowAIPrompt(true);
  }, []);

  // Handle AI image generation
  const handleAIGenerate = useCallback(async () => {
    if (!aiPrompt.trim()) {
      Alert.alert('Prompt Required', 'Please enter a description for the image you want to generate.');
      return;
    }

    try {
      setIsGeneratingImage(true);
      setShowAIPrompt(false);
      
      const response = await apis.generateAIImage(aiPrompt.trim());

      if (response && response.success && response.image) {
        setPostData(prev => ({
          ...prev,
          image: response.image
        }));
        setAiPrompt('');
        Alert.alert('Success', 'Image generated successfully!');
      } else {
        Alert.alert('Error', response?.message || 'Failed to generate image. Please try again.');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      Alert.alert('Error', 'Failed to generate image. Please try again.');
    } finally {
      setIsGeneratingImage(false);
    }
  }, [aiPrompt]);

  // Legacy pickImage function for backward compatibility (now opens modal)
  const pickImage = useCallback(() => {
    handleImagePress();
  }, [handleImagePress]);

  // Interactive editing handlers - Stable references
  const startEditing = useCallback((mode: 'caption' | 'headline' | 'location' | 'hashtags', currentValue: string = '') => {
    setEditingMode(mode);
    setTempValue(currentValue);
  }, []);

  const saveEdit = useCallback(() => {
    switch (editingMode) {
      case 'caption':
        setPostData(prev => ({ ...prev, caption: tempValue }));
        break;
      case 'headline':
        setPostData(prev => ({ ...prev, headline: tempValue }));
        break;
      case 'location':
        setPostData(prev => ({ ...prev, location: tempValue }));
        break;
      case 'hashtags':
        if (tempValue.trim()) {
          const newTags = tempValue.split(' ').map(tag => tag.replace('#', '').trim()).filter(tag => tag);
          setPostData(prev => ({ 
            ...prev, 
            hashtags: [...new Set([...prev.hashtags, ...newTags])]
          }));
        }
        break;
    }
    setEditingMode('none');
    setTempValue('');
  }, [editingMode, tempValue]);

  const cancelEdit = useCallback(() => {
    setEditingMode('none');
    setTempValue('');
  }, []);

  // Post actions - Stable references
  const handleSave = useCallback(() => onSave?.(postData), [postData, onSave]);
  const handlePreview = useCallback(() => {
    setShowPreview(true);
    onPreview?.(postData);
  }, [postData, onPreview]);
  
  const handlePublish = useCallback(() => {
    if (!postData.image) {
      Alert.alert('Image Required', 'Please add an image to your post.');
      return;
    }
    if (!postData.caption.trim()) {
      Alert.alert('Caption Required', 'Please add a caption to your post.');
      return;
    }
    onPublish?.(postData);
  }, [postData, onPublish]);

  // Stable handlers for interactive elements
  const handleLocationEdit = useCallback(() => startEditing('location', postData.location), [startEditing, postData.location]);
  const handleLocationAdd = useCallback(() => startEditing('location'), [startEditing]);
  const handleCaptionEdit = useCallback(() => startEditing('caption', postData.caption), [startEditing, postData.caption]);
  const handleCaptionAdd = useCallback(() => startEditing('caption'), [startEditing]);
  const handleHeadlineEdit = useCallback(() => startEditing('headline', postData.headline), [startEditing, postData.headline]);
  const handleHeadlineAdd = useCallback(() => startEditing('headline'), [startEditing]);
  const handleHashtagsEdit = useCallback(() => startEditing('hashtags', postData.hashtags.map(tag => `#${tag}`).join(' ')), [startEditing, postData.hashtags]);
  const handleHashtagsAdd = useCallback(() => startEditing('hashtags'), [startEditing]);
  
  // Sheet handlers
  const handleSheetClose = useCallback((open: boolean) => !open && cancelEdit(), [cancelEdit]);
  const handlePreviewSheetClose = useCallback(() => setShowPreview(false), []);

  // Instagram Post Preview Component - Stable reference
  const InstagramPostPreview = useCallback(() => (
    <View
      backgroundColor={theme ? 'white' : '#1a1a1a'}
      borderWidth={0}
      borderRadius={0}
      overflow="hidden"
      padding={20}
      maxWidth={400}
      marginHorizontal="auto"
      shadowColor={theme ? '#000' : '#fff'}
      shadowOffset={{ width: 0, height: 2 }}
      shadowOpacity={theme ? 0.1 : 0.2}
      shadowRadius={8}
      // elevation={3} // Removed unsupported prop
    >
      {/* Post Header - Instagram Style */}
      <XStack
        padding={16}
        alignItems="center"
        gap={12}
        backgroundColor={theme ? 'white' : '#1a1a1a'}
      >
        {/* Profile Picture */}
        <View
          style={{
            width: 40,
            height: 40,
            borderRadius: 20,
            backgroundColor: theme ? '#f0f0f0' : '#333',
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 2,
            borderColor: theme ? '#e5e5e5' : '#444',
          }}
        >
          <MaterialIcons 
            name="storefront" 
            size={24} 
            color={theme ? '#666' : '#aaa'} 
          />
        </View>

        {/* Username and Location */}
        <YStack flex={1} gap={2}>
          <XStack alignItems="center" gap={6}>
            <SizableText fontWeight="600" fontSize={14} color={theme ? '#000' : '#fff'}>
              your_business
            </SizableText>
            {postData.isSponsored && (
              <View
                style={{
                  backgroundColor: theme ? '#0066cc' : '#1e40af',
                  paddingHorizontal: 6,
                  paddingVertical: 2,
                  borderRadius: 4,
                }}
              >
                <SizableText fontSize={10} color="white" fontWeight="600">
                  Sponsored
                </SizableText>
              </View>
            )}
          </XStack>
          {postData.location ? (
            <Pressable onPress={handleLocationEdit}>
              <SizableText fontSize={12} color={theme ? '#666' : '#999'}>
                📍 {postData.location}
              </SizableText>
            </Pressable>
          ) : (
            <Pressable onPress={handleLocationAdd}>
              <View
                style={{
                  width: 80,
                  height: 12,
                  backgroundColor: theme ? '#f0f0f0' : '#333',
                  borderRadius: 6,
                  borderWidth: 1,
                  borderStyle: 'dashed',
                  borderColor: theme ? '#ccc' : '#555',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SizableText fontSize={8} color={theme ? '#999' : '#666'}>
                  + location
                </SizableText>
              </View>
            </Pressable>
          )}
        </YStack>

        {/* More Options */}
        <MaterialIcons 
          name="more-vert" 
          size={24} 
          color={theme ? '#666' : '#aaa'} 
        />
      </XStack>

      {/* Post Image */}
      <Pressable onPress={handleImagePress}>
        <View
          style={{
            aspectRatio: 1,
            backgroundColor: theme ? '#f8f9fa' : '#2a2a2a',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {postData.image ? (
            <Image
              source={{ uri: postData.image }}
              style={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
              }}
              resizeMode="cover"
              accessibilityLabel="Post content"
            />
          ) : (
            <YStack alignItems="center" gap={12}>
              <View
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 40,
                  backgroundColor: theme ? '#e9ecef' : '#404040',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderWidth: 2,
                  borderStyle: 'dashed',
                  borderColor: theme ? '#007AFF' : '#64b5f6',
                }}
              >
                <MaterialIcons 
                  name="add-photo-alternate" 
                  size={36} 
                  color={theme ? '#007AFF' : '#64b5f6'} 
                />
              </View>
              <SizableText 
                color={theme ? '#007AFF' : '#64b5f6'} 
                fontSize={14}
                textAlign="center"
                maxWidth={200}
                fontWeight="500"
              >
                Tap to add image
              </SizableText>
            </YStack>
          )}
          
          {/* Edit overlay for existing image */}
          {postData.image && (
            <View
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(0,0,0,0.6)',
                borderRadius: 20,
                padding: 8,
              }}
            >
              <MaterialIcons 
                name="edit" 
                size={16} 
                color="white" 
              />
            </View>
          )}
        </View>
      </Pressable>

      {/* Post Actions Bar */}
      <XStack
        padding={16}
        alignItems="center"
        justifyContent="space-between"
        backgroundColor={theme ? 'white' : '#1a1a1a'}
      >
        <XStack gap={20}>
          <FontAwesome5 
            name="heart" 
            size={24} 
            color={theme ? '#262626' : '#f5f5f5'} 
          />
          <FontAwesome5 
            name="comment" 
            size={24} 
            color={theme ? '#262626' : '#f5f5f5'} 
          />
          <FontAwesome5 
            name="share" 
            size={24} 
            color={theme ? '#262626' : '#f5f5f5'} 
          />
        </XStack>
        <FontAwesome5 
          name="bookmark" 
          size={24} 
          color={theme ? '#262626' : '#f5f5f5'} 
        />
      </XStack>

      {/* Likes Count */}
      <View style={{ paddingHorizontal: 16, paddingBottom: 8 }}>
        <SizableText fontWeight="600" fontSize={14} color={theme ? '#000' : '#fff'}>
          127 likes
        </SizableText>
      </View>

      {/* Post Content */}
      <YStack style={{ paddingHorizontal: 16, paddingBottom: 16 }} gap={8}>
        {/* Caption */}
        {postData.caption ? (
          <Pressable onPress={handleCaptionEdit}>
            <SizableText fontSize={14} lineHeight={20} color={theme ? '#000' : '#fff'}>
              <SizableText fontWeight="600">your_business</SizableText>{' '}
              {postData.caption}
            </SizableText>
          </Pressable>
        ) : (
          <Pressable onPress={handleCaptionAdd}>
            <YStack gap={4}>
              <XStack alignItems="center" gap={6}>
                <SizableText fontWeight="600" fontSize={14} color={theme ? '#000' : '#fff'}>
                  your_business
                </SizableText>
                <View
                  style={{
                    flex: 1,
                    height: 14,
                    backgroundColor: theme ? '#e3f2fd' : '#1e3a8a',
                    borderRadius: 7,
                    maxWidth: 200,
                    borderWidth: 1,
                    borderStyle: 'dashed',
                    borderColor: theme ? '#007AFF' : '#64b5f6',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <SizableText fontSize={8} color={theme ? '#007AFF' : '#64b5f6'}>
                    + add caption
                  </SizableText>
                </View>
              </XStack>
              <View
                style={{
                  width: '80%',
                  height: 14,
                  backgroundColor: theme ? '#f0f0f0' : '#333',
                  borderRadius: 7,
                }}
              />
            </YStack>
          </Pressable>
        )}

        {/* Headline */}
        {postData.headline ? (
          <Pressable onPress={handleHeadlineEdit}>
            <SizableText fontWeight="600" fontSize={14} color={theme ? '#000' : '#fff'}>
              "{postData.headline}"
            </SizableText>
          </Pressable>
        ) : (
          <Pressable onPress={handleHeadlineAdd}>
            <View
              style={{
                padding: 8,
                backgroundColor: theme ? '#e3f2fd' : '#1e3a8a',
                borderRadius: 8,
                borderWidth: 1,
                borderStyle: 'dashed',
                borderColor: theme ? '#007AFF' : '#64b5f6',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SizableText fontSize={12} color={theme ? '#007AFF' : '#64b5f6'} fontWeight="500">
                + add headline
              </SizableText>
            </View>
          </Pressable>
        )}

        {/* Hashtags */}
        {postData.hashtags.length > 0 ? (
          <Pressable onPress={handleHashtagsEdit}>
            <SizableText fontSize={14} color="#1877f2" lineHeight={20}>
              {postData.hashtags.map(tag => `#${tag}`).join(' ')}
            </SizableText>
          </Pressable>
        ) : (
          <Pressable onPress={handleHashtagsAdd}>
            <XStack gap={8} flexWrap="wrap">
              {[1, 2, 3].map((_, index) => (
                <View
                  key={index}
                  style={{
                    width: 60 + Math.random() * 40,
                    height: 14,
                    backgroundColor: theme ? '#e3f2fd' : '#1e3a8a',
                    borderRadius: 7,
                    borderWidth: 1,
                    borderStyle: 'dashed',
                    borderColor: theme ? '#007AFF' : '#64b5f6',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {index === 1 && (
                    <SizableText fontSize={8} color={theme ? '#007AFF' : '#64b5f6'}>
                      + hashtags
                    </SizableText>
                  )}
                </View>
              ))}
            </XStack>
          </Pressable>
        )}

        {/* Comments Preview */}
        <YStack gap={4} marginTop={4}>
          <SizableText fontSize={14} color={theme ? '#8e8e8e' : '#999'}>
            View all 23 comments
          </SizableText>
          
          {/* Sample Comment */}
          <XStack gap={6} alignItems="flex-start">
            <SizableText fontWeight="600" fontSize={13} color={theme ? '#000' : '#fff'}>
              sarah_m
            </SizableText>
            <SizableText fontSize={13} color={theme ? '#000' : '#fff'} flex={1}>
              Love this! 😍
            </SizableText>
          </XStack>
        </YStack>

        {/* Time Posted */}
        <SizableText fontSize={12} color={theme ? '#8e8e8e' : '#999'} marginTop={8}>
          2 HOURS AGO
        </SizableText>
      </YStack>
    </View>
  ), [theme, postData, handleLocationEdit, handleLocationAdd, handleCaptionEdit, handleCaptionAdd, handleHeadlineEdit, handleHeadlineAdd, handleHashtagsEdit, handleHashtagsAdd, handleImagePress]);

  return (
    <ScrollView style={{ flex: 1 }}>
      <YStack gap={20}>
        {/* Interactive Preview */}
        <Card backgroundColor={theme ? 'white' : '#1a1a1a'}>
          <InstagramPostPreview />
        </Card>

        {/* Action Buttons */}
        <XStack gap={12} marginTop={20}>
          <Button
            flex={1}
            backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
            color="white"
            borderRadius={12}
            icon={<MaterialIcons name="save" size={18} color="white" />}
            onPress={handleSave}
            paddingVertical={14}
            pressStyle={{ backgroundColor: theme ? "#0284c7" : "#0ea5e9" }}
          >
            Save Draft
          </Button>
          <Button
            flex={1}
            backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
            color="white"
            borderRadius={12}
            icon={<MaterialIcons name="preview" size={18} color="white" />}
            onPress={handlePreview}
            paddingVertical={14}
            pressStyle={{ backgroundColor: theme ? "#7c3aed" : "#5b21b6" }}
          >
            Preview
          </Button>
          <Button
            flex={1}
            backgroundColor={theme ? "#10b981" : "#059669"}
            color="white"
            borderRadius={12}
            icon={<MaterialIcons name="publish" size={18} color="white" />}
            onPress={handlePublish}
            paddingVertical={14}
            pressStyle={{ backgroundColor: theme ? "#059669" : "#047857" }}
          >
            Publish
          </Button>
        </XStack>

        {/* Interactive Editing Modal */}
        <Sheet
          modal
          open={editingMode !== 'none'}
          onOpenChange={handleSheetClose}
          snapPointsMode="fit"
          dismissOnSnapToBottom
        >
          <Sheet.Overlay
            animation="lazy"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Sheet.Handle />
          <Sheet.Frame padding={20}>
            <YStack gap={16}>
              <H4>
                Edit {editingMode === 'caption' ? 'Caption' : 
                     editingMode === 'headline' ? 'Headline' :
                     editingMode === 'location' ? 'Location' : 'Hashtags'}
              </H4>
              
              {editingMode === 'caption' && (
                <YStack gap={8}>
                  <SizableText fontSize={12} color={theme ? '#666' : '#999'}>
                    Write your caption (up to 2200 characters)
                  </SizableText>
                  <TextInput
                    style={{
                      borderWidth: 1,
                      borderColor: theme ? '#ddd' : '#444',
                      borderRadius: 8,
                      padding: 12,
                      minHeight: 100,
                      fontSize: 16,
                      color: theme ? '#000' : '#fff',
                      backgroundColor: theme ? 'white' : '#1a1a1a',
                      textAlignVertical: 'top',
                    }}
                    placeholder="Write your caption here..."
                    placeholderTextColor={theme ? '#999' : '#666'}
                    value={tempValue}
                    onChangeText={setTempValue}
                    multiline
                    maxLength={2200}
                    autoFocus
                  />
                  <SizableText fontSize={10} color={theme ? '#666' : '#999'}>
                    {tempValue.length}/2200 characters
                  </SizableText>
                </YStack>
              )}

              {editingMode === 'headline' && (
                <YStack gap={8}>
                  <SizableText fontSize={12} color={theme ? '#666' : '#999'}>
                    Add a catchy headline (up to 100 characters)
                  </SizableText>
                  <TextInput
                    style={{
                      borderWidth: 1,
                      borderColor: theme ? '#ddd' : '#444',
                      borderRadius: 8,
                      padding: 12,
                      fontSize: 16,
                      color: theme ? '#000' : '#fff',
                      backgroundColor: theme ? 'white' : '#1a1a1a',
                    }}
                    placeholder="Enter a catchy headline..."
                    placeholderTextColor={theme ? '#999' : '#666'}
                    value={tempValue}
                    onChangeText={setTempValue}
                    maxLength={100}
                    autoFocus
                  />
                  <SizableText fontSize={10} color={theme ? '#666' : '#999'}>
                    {tempValue.length}/100 characters
                  </SizableText>
                </YStack>
              )}

              {editingMode === 'location' && (
                <YStack gap={8}>
                  <SizableText fontSize={12} color={theme ? '#666' : '#999'}>
                    Add a location to your post
                  </SizableText>
                  <TextInput
                    style={{
                      borderWidth: 1,
                      borderColor: theme ? '#ddd' : '#444',
                      borderRadius: 8,
                      padding: 12,
                      fontSize: 16,
                      color: theme ? '#000' : '#fff',
                      backgroundColor: theme ? 'white' : '#1a1a1a',
                    }}
                    placeholder="Add location..."
                    placeholderTextColor={theme ? '#999' : '#666'}
                    value={tempValue}
                    onChangeText={setTempValue}
                    maxLength={100}
                    autoFocus
                  />
                </YStack>
              )}

              {editingMode === 'hashtags' && (
                <YStack gap={8}>
                  <SizableText fontSize={12} color={theme ? '#666' : '#999'}>
                    Add hashtags separated by spaces (without #)
                  </SizableText>
                  <TextInput
                    style={{
                      borderWidth: 1,
                      borderColor: theme ? '#ddd' : '#444',
                      borderRadius: 8,
                      padding: 12,
                      fontSize: 16,
                      color: theme ? '#000' : '#fff',
                      backgroundColor: theme ? 'white' : '#1a1a1a',
                    }}
                    placeholder="design marketing socialmedia..."
                    placeholderTextColor={theme ? '#999' : '#666'}
                    value={tempValue}
                    onChangeText={setTempValue}
                    autoFocus
                  />
                  <SizableText fontSize={10} color={theme ? '#666' : '#999'}>
                    Tip: Separate multiple hashtags with spaces
                  </SizableText>
                </YStack>
              )}

              <XStack gap={12}>
                <Button
                  flex={1}
                  variant="outlined"
                  onPress={cancelEdit}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  onPress={saveEdit}
                  backgroundColor="$blue10"
                >
                  Save
                </Button>
              </XStack>
            </YStack>
          </Sheet.Frame>
        </Sheet>

        {/* Preview Modal */}
        <Sheet
          modal
          open={showPreview}
          onOpenChange={setShowPreview}
          snapPointsMode="fit"
          dismissOnSnapToBottom
        >
          <Sheet.Overlay
            animation="lazy"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Sheet.Handle />
          <Sheet.Frame padding={20}>
            <YStack gap={16}>
              <H4>Post Preview</H4>
              <InstagramPostPreview />
              <Button onPress={handlePreviewSheetClose}>
                Close Preview
              </Button>
            </YStack>
          </Sheet.Frame>
        </Sheet>

        {/* Media Selection Modal */}
        <Sheet
          modal
          open={showMediaSelection}
          onOpenChange={setShowMediaSelection}
          snapPointsMode="fit"
          dismissOnSnapToBottom
        >
          <Sheet.Overlay
            animation="lazy"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Sheet.Handle />
          <Sheet.Frame padding={20}>
            <YStack gap={20}>
              <H4>Add Image</H4>
              <SizableText fontSize={14} color={theme ? '#666' : '#999'}>
                Choose how you want to add an image to your post
              </SizableText>
              
              <YStack gap={12}>
                <Button
                  size="$4"
                  backgroundColor={theme ? "#0ea5e9" : "#38bdf8"}
                  color="white"
                  borderRadius={12}
                  icon={<MaterialIcons name="photo-library" size={20} color="white" />}
                  onPress={uploadFromDevice}
                  paddingVertical={16}
                  pressStyle={{ backgroundColor: theme ? "#0284c7" : "#0ea5e9" }}
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload from Device'}
                </Button>
                
                <Button
                  size="$4"
                  backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                  color="white"
                  borderRadius={12}
                  icon={<MaterialIcons name="auto-awesome" size={20} color="white" />}
                  onPress={generateViaAI}
                  paddingVertical={16}
                  pressStyle={{ backgroundColor: theme ? "#7c3aed" : "#5b21b6" }}
                >
                  Generate via AI
                </Button>
              </YStack>

              <Button
                variant="outlined"
                onPress={() => setShowMediaSelection(false)}
              >
                Cancel
              </Button>
            </YStack>
          </Sheet.Frame>
        </Sheet>

        {/* AI Prompt Modal */}
        <Sheet
          modal
          open={showAIPrompt}
          onOpenChange={setShowAIPrompt}
          snapPointsMode="fit"
          dismissOnSnapToBottom
        >
          <Sheet.Overlay
            animation="lazy"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
          />
          <Sheet.Handle />
          <Sheet.Frame padding={20}>
            <YStack gap={16}>
              <H4>Generate Image with AI</H4>
              <SizableText fontSize={12} color={theme ? '#666' : '#999'}>
                Describe the image you want to generate. Be as detailed as possible for best results.
              </SizableText>
              
              <YStack gap={8}>
                <TextInput
                  style={{
                    borderWidth: 1,
                    borderColor: theme ? '#ddd' : '#444',
                    borderRadius: 8,
                    padding: 12,
                    minHeight: 100,
                    fontSize: 16,
                    color: theme ? '#000' : '#fff',
                    backgroundColor: theme ? 'white' : '#1a1a1a',
                    textAlignVertical: 'top',
                  }}
                  placeholder="e.g., A modern minimalist workspace with a laptop, plants, and natural lighting..."
                  placeholderTextColor={theme ? '#999' : '#666'}
                  value={aiPrompt}
                  onChangeText={setAiPrompt}
                  multiline
                  autoFocus
                />
                <SizableText fontSize={10} color={theme ? '#666' : '#999'}>
                  {aiPrompt.length} characters
                </SizableText>
              </YStack>

              {isGeneratingImage && (
                <XStack alignItems="center" gap={8} justifyContent="center" paddingVertical={8}>
                  <ActivityIndicator size="small" color={theme ? '#8b5cf6' : '#7c3aed'} />
                  <SizableText fontSize={14} color={theme ? '#666' : '#999'}>
                    Generating image... This may take a moment.
                  </SizableText>
                </XStack>
              )}

              <XStack gap={12}>
                <Button
                  flex={1}
                  variant="outlined"
                  onPress={() => {
                    setShowAIPrompt(false);
                    setAiPrompt('');
                  }}
                  disabled={isGeneratingImage}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  onPress={handleAIGenerate}
                  backgroundColor={theme ? "#8b5cf6" : "#7c3aed"}
                  color="white"
                  disabled={isGeneratingImage || !aiPrompt.trim()}
                >
                  {isGeneratingImage ? 'Generating...' : 'Generate'}
                </Button>
              </XStack>
            </YStack>
          </Sheet.Frame>
        </Sheet>
      </YStack>
    </ScrollView>
  );
};

// Set display name for debugging
PostDesigner.displayName = 'PostDesigner';

export default PostDesigner;
